import { Beech } from './beech';
import * as AgentProtocol from './agent-protocol';
import { MetricsSample } from './metrics';
import { collectEnvironment } from './environment';

// Dev banyan host. Switchable from settings.vue (Electron only) via the
// useDevBanyan persist flag. The production host comes from beech.config.listen_host
// which is baked at build time.
export const DEV_BANYAN_HOST = 'banyan-local.halocare-platform.com';

export type ServerState = 'INIT' | 'OPENING' | 'OPEN' | 'WAIT';

export interface InviteSelection {
	guid?: string;
	name?: string;
	number?: string;
	digits?: string;
	grandma?: string;
}

export default class Server {
	private readonly url: string;

	private beech: Beech;
	private socket: WebSocket | null = null;
	private syncer: number;
	private reconnectToken: string | null = null;

	// Set when the session times out (13h). Stops the auto-reconnect loop so the
	// logout actually sticks — otherwise beech would silently reconnect + re-auth
	// via the saved token within a second and never leave the roster. Cleared by
	// reconnectNow(), when the agent deliberately comes back.
	private suspendReconnect = false;

	// Guarantees sign-out reaches a logged-out state even if banyan never closes the
	// socket (lost frame / server error). Armed by signOut(), cleared by onSocketClose.
	private signOutFallbackTimer: number | null = null;

	private static readonly MAX_QUEUE_SIZE = 100;
	private message_queue: string[] = [];

	private static readonly CLIENT_LOG_THROTTLE_MS = 60_000;
	private lastClientLogAt = 0;
	private lastClientLogSummary = '';
	private lastEnvHash = '';
	private debugDropTimer: number | null = null;

	public constructor(beech: Beech) {
		logg.log('server >> init');
		this.beech = beech;
		this.reconnectToken = beech.persist.getReconnectToken();

		// SECURITY: Only consult the dev-banyan toggle when running inside Electron.
		// window.electronAPI is exposed by electron/preload.ts via contextBridge and is
		// undefined in browser builds, so a user on the production web app cannot
		// trigger the dev path even if they manage to set settings:useDevBanyan in
		// localStorage. The Vue template gate (v-if="isElectron" in settings.vue) is
		// cosmetic only — this is the actual security boundary.
		const isElectron = window.electronAPI?.isElectron === true;
		const useDevBanyan = isElectron && beech.persist.getUseDevBanyan();
		const host = useDevBanyan ? DEV_BANYAN_HOST : beech.config.listen_host;

		let urlPrefix = 'wss://';
		if (this.beech.config.environment === 'development') urlPrefix = 'ws://';
		this.url = `${urlPrefix}${host}/v1/agent`;
		console.log(`Websocket URL: ${this.url}${useDevBanyan ? ' (dev banyan)' : ''}`);

		// every 10 seconds, sync our status with the server
		this.syncer = window.setInterval(this.doSync, 10000);
	}

	public connect = () => {
		this.reconnect();
	};

	private closeSocket = () => {
		if (!this.socket) return;
		this.socket.removeEventListener('error', this.onSocketError);
		this.socket.removeEventListener('open', this.onSocketOpen);
		this.socket.removeEventListener('message', this.onSocketMessage);
		this.socket.removeEventListener('close', this.onSocketClose);
		this.socket.close();
		this.socket = null;
	};

	private reconnect = () => {
		this.closeSocket();
		this.beech.model.setServerState('OPENING');

		let url = this.url;
		if (this.reconnectToken) {
			url += `?reconnectToken=${this.reconnectToken}`;
		}

		this.socket = new WebSocket(url);
		this.socket.addEventListener('error', this.onSocketError);
		this.socket.addEventListener('open', this.onSocketOpen);
		this.socket.addEventListener('message', this.onSocketMessage);
		this.socket.addEventListener('close', this.onSocketClose);
	};

	public doLoginPassword = (email: string, password: string) => {
		this.sendCmd('LOGIN', {
			email: email,
			password: password,
			session: ''
		});
	};

	public doLoginSession = (email: string, session: string) => {
		this.sendCmd('LOGIN', {
			email: email,
			password: '',
			session: session
		});
	};

	public onClientInfoChanged = () => {
		this.doSendClientInfo();
	};

	public doSync = () => {
		// Timed out: socket is closed and not coming back until Resume — don't queue
		// SYNCs nobody will read (they'd just churn the queue / flush stale on resume).
		if (this.suspendReconnect) return;
		this.sendCmd('SYNC', {
			agentState: this.beech.model.getAgentState(),
			headsetState: this.beech.model.getHeadsetState(),
			// Subscribe to banyan's invite-targets feed only while on a call (when the invite drawer is usable).
			sendInviteTargets: this.beech.model.getAgentState() === 'TALKING'
		});
		this.sendEnvIfChanged();
	};

	// On each sync tick, send the env snapshot only when it changed — gives banyan a
	// state timeline (and a crash-resilient baseline) without flooding. Logs stay error-triggered.
	public sendEnvIfChanged = () => {
		try {
			if (this.beech.model.getData().serverState !== 'OPEN') return;
			const env = collectEnvironment(this.beech);
			const hash = JSON.stringify({ ...env, capturedAt: '' });
			if (hash === this.lastEnvHash) return;
			this.lastEnvHash = hash;
			const payload: AgentProtocol.ClientLogCmd = { reason: 'env', env: env, log: '' };
			logg.debug('server >> sendEnv');
			this.socket?.send('CLIENT_LOG:' + JSON.stringify(payload));
		} catch (e) {
			console.error('server >> sendEnvIfChanged failed', e);
		}
	};

	public logQualityWarning = (callSid: string, warningName: string, active: boolean) => {
		this.sendCmd('QUALITY_WARNING', {
			callSid: callSid,
			warningName: warningName,
			active: active
		});
	};

	public logTwilioError = (code: number, message: string) => {
		this.sendCmd('TWILIO_ERROR', {
			code: code,
			message: message
		});
	};

	// Bypasses sendCmd on purpose: sendCmd debug-logs its payload, which would push
	// the whole ~500KB log back into the ring and re-send it next time.
	public sendClientLog = (reason: string, errorSummary: string = '') => {
		try {
			if (this.beech.model.getData().serverState !== 'OPEN') {
				return;
			}
			if (reason !== 'manual') {
				const now = Date.now();
				const isRepeat = errorSummary === this.lastClientLogSummary;
				if (isRepeat && now - this.lastClientLogAt < Server.CLIENT_LOG_THROTTLE_MS) {
					return;
				}
				this.lastClientLogAt = now;
				this.lastClientLogSummary = errorSummary;
			}
			const payload: AgentProtocol.ClientLogCmd = {
				reason: reason,
				errorSummary: errorSummary || undefined,
				env: collectEnvironment(this.beech),
				log: logg.getRecentLog()
			};
			logg.debug('server >> sendClientLog', reason, errorSummary);
			this.socket?.send('CLIENT_LOG:' + JSON.stringify(payload));
		} catch (e) {
			console.error('server >> sendClientLog failed', e);
		}
	};

	public logMetrics = (callSid: string, data: MetricsSample) => {
		this.sendCmd('CALL_METRICS', {
			callSid: callSid,
			timestamp: data.timestamp,
			bytesSent: data.bytesSent,
			bytesReceived: data.bytesReceived,
			packetsSent: data.packetsSent,
			packetsReceived: data.packetsReceived,
			packetsLost: data.packetsLost,
			fractionLost: data.fractionLost,
			jitter: data.jitter,
			remoteAudioLevel: data.remoteAudioLevel,
			localAudioLevel: data.localAudioLevel,
			roundTripTime: data.roundTripTime
		});
	};

	public modifyCallHold = (callSid: string, holding: boolean) => {
		this.sendCmd('CALL_HOLD', {
			callSid: callSid,
			hold: holding
		});
	};

	public modifyCallMute = (callSid: string, connectionGuid: string, muted: boolean) => {
		this.sendCmd('CALL_MUTE', {
			callSid: callSid,
			targetGuid: connectionGuid,
			mute: muted
		});
	};

	public inviteAgentToCall = (callSid: string, agentGuid: string) => {
		this.sendCmd('CALL_INVITE', {
			callSid: callSid,
			type: 'agent',
			targetGuid: agentGuid
		});
	};

	public inviteGrandmaToCall = (callSid: string, grandmaId: number) => {
		this.sendCmd('CALL_INVITE', {
			callSid: callSid,
			type: 'grandma',
			grandmaId: grandmaId
		});
	};

	public inviteExternalToCall = (
		callSid: string,
		external: {
			number: string;
			digits: string | null;
			name: string | null;
		}
	) => {
		const override = this.beech.twilio.getCallerId();
		this.sendCmd('CALL_INVITE', {
			callSid: callSid,
			type: 'pstn',
			dialTo: external.number,
			...(override ? { callerIdOverride: override } : {}),
			dialDigits: external.digits,
			dialName: external.name
		});
	};

	public doKickFromCall = (callSid: string, connectionGuid: string) => {
		this.sendCmd('CALL_KICK', {
			callSid: callSid,
			targetGuid: connectionGuid
		});
	};

	public claimCall = (guid: string) => {
		this.sendCmd('CALL_CLAIM', { guid });
	};

	public setAgentState = (guid: string, state: AgentProtocol.AgentState) => {
		this.sendCmd('AGENT_STATE_UPDATE', { guid: guid, state: state });
	};

	public listContacts = () => {
		this.sendCmd('LIST_CONTACTS', {});
	};

	public createContact = (name: string, phone: string, region: string) => {
		this.sendCmd('CREATE_CONTACT', { name, phone, region });
	};

	public updateContact = (id: number, name: string, phone: string, region: string) => {
		this.sendCmd('UPDATE_CONTACT', { id, name, phone, region });
	};

	/* ** */

	private onSocketError = (ev: Event) => {
		logg.warn('server >> socket error', ev);
	};

	private onSocketOpen = (ev: Event) => {
		logg.info('server >> open', ev);

		this.beech.model.setServerState('OPEN');
		this.cancelSimulatedDrop();

		while (this.message_queue.length > 0) {
			this.socket?.send(this.message_queue.shift()!);
		}
	};

	private onSocketClose = (ev: CloseEvent) => {
		logg.warn('server >> close', ev);

		this.beech.model.setServerState('WAIT');
		this.cancelSimulatedDrop();

		// banyan closed us (the expected sign-out path) — the fallback isn't needed.
		if (this.signOutFallbackTimer) {
			window.clearTimeout(this.signOutFallbackTimer);
			this.signOutFallbackTimer = null;
		}

		if (this.suspendReconnect) {
			logg.log('server >> reconnect suspended (session timed out)');
			return;
		}

		window.setTimeout(this.reconnect, 1000);
	};

	// Re-enable reconnection (after a timeout suspend) and reconnect now. Used both
	// to resume a timed-out session and to start a fresh login after discarding it.
	// With the dead reconnectToken cleared, HELLO routes to the saved session token
	// if one remains (silent re-auth), otherwise to the normal login screen.
	public reconnectNow = () => {
		logg.log('server >> reconnectNow');
		this.suspendReconnect = false;
		this.reconnect();
	};

	// Deliberate sign-out (settings). The next agent on a shared machine must log in
	// as themselves, so this both revokes the session server-side and guarantees a
	// logged-out terminal state on this client.
	public signOut = async () => {
		logg.log('server >> signOut');

		// Clear local credentials FIRST, so a fast banyan-driven reconnect can't
		// re-read and auto-login with the token we're about to revoke.
		this.reconnectToken = null;
		this.beech.persist.clearReconnectToken();
		this.suspendReconnect = false;
		await this.beech.persist.clearSessionToken();

		// Ask banyan to revoke the session. We do NOT close the socket here — a
		// client close can drop the buffered SIGN_OUT before delivery. banyan revokes
		// then closes, and onSocketClose reconnects into a clean login.
		this.sendCmd('SIGN_OUT', {});

		// Fallback: if banyan never closes (lost frame / server error), force the
		// transition so "logged out" is guaranteed. The delay is well past SIGN_OUT
		// flushing, so there's no close-before-flush race here.
		this.signOutFallbackTimer = window.setTimeout(() => {
			logg.warn('server >> signOut: no server close within grace period, forcing reconnect');
			this.signOutFallbackTimer = null;
			this.reconnect();
		}, 3000);
	};

	private onSocketMessage = (ev: MessageEvent) => {
		// Messages ought to be of the form <type>:<JSON>
		let col_idx = ev.data.indexOf(':');
		if (col_idx < 1) {
			logg.warn('server >> invalid message (format)', ev.data, ev);

			throw new Error('server >> invalid message (format)');
		}

		let type = ev.data.substring(0, col_idx);
		let data: any;

		try {
			data = JSON.parse(ev.data.substring(col_idx + 1));
		} catch (e) {
			logg.warn('server >> invalid message (json)', ev.data, ev);

			throw new Error('server >> invalid message (json) ' + e);
		}

		this.handleCmd(type, data);
	};

	private handleCmd<K extends keyof AgentProtocol.CommandMap>(cmd: K, data: AgentProtocol.CommandMap[K]) {
		logg.info('server >> cmd', cmd, data);

		switch (cmd) {
			case 'HELLO':
				this.handleHello(<AgentProtocol.HelloCmd>data);
				break;

			case 'RECONNECT_ERROR':
				this.handleReconnectError(<AgentProtocol.ReconnectErrorCmd>data);
				break;

			case 'LOGIN_ERROR':
				this.handleLoginError(<AgentProtocol.LoginErrorCmd>data);
				break;

			case 'LOGOUT':
				this.handleLogout(<AgentProtocol.LogoutCmd>data);
				break;

			case 'REFRESH':
				this.handleRefresh(<AgentProtocol.RefreshCmd>data);
				break;

			case 'RELOAD':
				this.handleReload(<AgentProtocol.ReloadCmd>data);
				break;

			case 'HEADSET_OVERRIDE':
				this.handleHeadsetOverride(<AgentProtocol.HeadsetOverrideCmd>data);
				break;

			case 'POOL_INFO':
				this.handlePoolInfo(<AgentProtocol.PoolInfoCmd>data);
				break;

			case 'TEAMS_INFO':
				this.handleTeamsInfo(<AgentProtocol.TeamsInfoCmd>data);
				break;

			case 'INVITE_TARGETS':
				this.handleInviteTargets(<AgentProtocol.InviteTargetsCmd>data);
				break;

			case 'CONTACTS_INFO':
				this.handleContactsInfo(<AgentProtocol.ContactsInfoCmd>data);
				break;

			case 'CONTACT_ERROR':
				this.handleContactError(<AgentProtocol.ContactErrorCmd>data);
				break;

			case 'CALL_INFO':
				this.handleCallInfo(<AgentProtocol.CallInfoCmd>data);
				break;

			case 'ERROR':
				this.handleError(<AgentProtocol.ErrorCmd>data);
				break;

			case 'LOBBY_INFO':
				this.handleLobbyInfo(<AgentProtocol.LobbyInfoCmd>data);
				break;

			case 'DIVERT_STATUS':
				this.handleDivertStatus(<AgentProtocol.DivertStatusCmd>data);
				break;

			default:
				logg.error('bad server command `' + cmd + '`');
				break;
		}
	}

	private async handleHello(data: AgentProtocol.HelloCmd) {
		this.doSendClientInfo();

		// HELLO means banyan no longer knows us — the reconnectToken is dead. Re-establish
		// presence deliberately rather than reconnecting into a nameless agent (AVAILABLE·NULL).
		if (this.reconnectToken) {
			const flow = this.beech.model.getAgentFlow();
			const inCall = flow === 'RINGING' || flow === 'TALKING' || flow === 'WRAPUP';

			// Standalone desktop, not on a call: one-click Resume instead of silently
			// resurrecting a session banyan let go. Embedded (wisteria drives its own
			// re-login) and in-call both fall through to re-auth below.
			if (!inCall && !this.beech.embedded.isEnabled()) {
				this.dropLivePresence();
				this.beech.flow.onSessionTimedOut();
				// Close the nameless agent's socket so it can't linger AVAILABLE on banyan
				// (queued SYNCs already flushed on open) or send a 12h REFRESH while parked.
				this.closeSocket();
				this.beech.model.setServerState('WAIT');
				return;
			}

			this.reconnectToken = null;
			this.beech.persist.clearReconnectToken();
		}

		// Remembered login: launching the app is a deliberate human action, so
		// auto-logging-in with a saved session is safe (unlike a silent reconnect).
		// If the keychain read fails, fall through to the normal login screen.
		const email = this.beech.persist.getLastEmail();
		const sessionToken = await this.beech.persist.getSessionToken().catch(() => null);
		if (email && sessionToken) {
			logg.info('server >> auto-login via saved session');
			this.beech.flow.doLoginSession(email, sessionToken);
			return;
		}

		this.beech.flow.onServerHello();
	}

	// suspendReconnect must be set before any flow change: setAgentFlow triggers doSync, which it gates.
	private dropLivePresence() {
		this.suspendReconnect = true;
		this.reconnectToken = null;
		this.beech.persist.clearReconnectToken();
	}

	private doSendClientInfo() {
		this.sendCmd('CLIENT_INFO', {
			appVersion: this.beech.config.release,
			appUserAgent: navigator.userAgent,
			parentApp: this.beech.embedded.getParentApp()
		});
	}

	private handleReconnectError(data: AgentProtocol.ReconnectErrorCmd) {
		this.beech.flow.onServerHello();
	}

	private handleLogout(data: AgentProtocol.LogoutCmd) {
		logg.warn('server >> LOGOUT', data.reason);

		// Session timed out (13h). Drop live presence but KEEP the saved session token so the
		// agent can resume with one click (no password), the way a desktop app stays signed in.
		this.dropLivePresence();
		this.beech.flow.onSessionTimedOut();
	}

	private handleLoginError(data: AgentProtocol.LoginErrorCmd) {
		// A stale/expired saved session must not loop us back into auto-login.
		void this.beech.persist.clearSessionToken();
		this.beech.flow.onLoginError(data.msg);
	}

	private handleRefresh(data: AgentProtocol.RefreshCmd) {
		if (typeof data.twilioToken === 'undefined') {
			logg.error('invalid REFRESH command');
			return;
		}

		this.reconnectToken = data.reconnectToken;
		if (this.reconnectToken) {
			this.beech.persist.setReconnectToken(this.reconnectToken);
		}
		if (data.sessionToken) {
			void this.beech.persist.setSessionToken(data.sessionToken);
		}
		this.beech.flow.onServerReady(data);
		this.doSync();
	}

	private handleReload(data: AgentProtocol.ReloadCmd) {
		let state = this.beech.model.getAgentState();
		if (state != 'RINGING' && state != 'TALKING') {
			if (this.beech.embedded.isEnabled()) {
				this.beech.embedded.doReload();
			} else {
				window.location.reload();
			}
		}
	}

	private handleHeadsetOverride(data: AgentProtocol.HeadsetOverrideCmd) {
		this.beech.model.setHeadsetState(data.headsetState);
	}

	private handleCallInfo(data: AgentProtocol.CallInfoCmd) {
		this.beech.flow.addCallInfo(data);
	}

	private handlePoolInfo(data: AgentProtocol.PoolInfoCmd) {
		this.beech.model.setPool(data);
	}

	private handleTeamsInfo(data: AgentProtocol.TeamsInfoCmd) {
		this.beech.model.setTeams(data.teams);
	}

	private handleInviteTargets(data: AgentProtocol.InviteTargetsCmd) {
		this.beech.model.setInviteTargets(data.targets);
	}

	private handleContactsInfo(data: AgentProtocol.ContactsInfoCmd) {
		this.beech.model.setContacts(data.contacts);
	}

	private handleContactError(data: AgentProtocol.ContactErrorCmd) {
		this.beech.model.setContactError(data.msg);
	}

	private handleError(data: AgentProtocol.ErrorCmd) {
		logg.warn('server >> protocol error', data);

		this.socket?.close();
	}

	private handleLobbyInfo(data: AgentProtocol.LobbyInfoCmd) {
		this.beech.model.setLobby(data.calls);
	}

	private handleDivertStatus(data: AgentProtocol.DivertStatusCmd) {
		this.beech.model.setDivertStatus(data.active);
	}

	public activateDivert() {
		this.sendCmd('DIVERT_ACTIVATE', {});
	}

	public deactivateDivert() {
		this.sendCmd('DIVERT_DEACTIVATE', {});
	}

	public toggleTranslation(callSid: string) {
		this.sendCmd('TRANSLATION_TOGGLE', {
			callSid: callSid
		});
	}

	private sendCmd = <K extends keyof AgentProtocol.CommandMap>(cmd: K, data: AgentProtocol.CommandMap[K]) => {
		logg.debug('server >> sendCmd', cmd, data);

		if (this.beech.model.getData().serverState === 'OPEN') {
			this.socket?.send(cmd + ':' + JSON.stringify(data));
		} else {
			if (this.message_queue.length < Server.MAX_QUEUE_SIZE) {
				this.message_queue.push(cmd + ':' + JSON.stringify(data));
			} else {
				logg.warn('server >> message queue full, dropping', cmd);
			}
		}
	};

	public scheduleSimulatedDrop = (ms: number) => {
		if (this.beech.config.environment !== 'development') {
			logg.debug('server >> debug >> scheduleSimulatedDrop skipped (env not development)');
			return;
		}

		if (!this.socket) {
			logg.warn('server >> debug >> scheduleSimulatedDrop skipped (no socket)');
			return;
		}

		this.cancelSimulatedDrop();

		logg.warn(`server >> debug >> scheduling websocket close in ${ms}ms`);
		this.debugDropTimer = window.setTimeout(() => {
			if (!this.socket) {
				logg.warn('server >> debug >> simulated drop fired but socket already null');
				this.debugDropTimer = null;
				return;
			}

			logg.warn('server >> debug >> closing websocket to simulate drop');
			this.socket.close(4000, 'debug simulated drop');
			this.debugDropTimer = null;
		}, ms);
	};

	public cancelSimulatedDrop = () => {
		if (this.debugDropTimer) {
			window.clearTimeout(this.debugDropTimer);
			this.debugDropTimer = null;
			logg.debug('server >> debug >> cleared simulated drop timer');
		}
	};
}
